import { useSuspenseQuery } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { StatDisplay } from "components/StatDisplay";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { getManagementMethodCodeDescription } from "encamp-shared/src/waste/validCodes";
import { gql } from "generated-graphql";
import { DestinationViewType } from "generated-graphql/graphql";
import { formatAmountInPounds } from "util/formatNumber";

import { useWasteFacilityState } from "../../WasteFacilityContext";
import { DestinationsView } from "./DestinationsView";

// A compact card per destination for the common small-count case; above this
// many distinct destinations, fall back to the by-destination grid so large
// tenants (Amazon/UPS) stay scannable.
const DESTINATION_CARD_THRESHOLD = 8;

// Per-(destination, management method) rows so each destination card can break
// its amount down by management method.
const WASTE_STREAM_DESTINATION_CARDS = gql(`
  query WasteStreamDestinationCards(
    $wasteStreamId: ID!
    $view: FacilityWasteView
    $startDate: Date
    $endDate: Date
  ) {
    wasteStreamDestinations(
      wasteStreamId: $wasteStreamId
      view: $view
      viewType: DESTINATIONS_BY_MANAGEMENT_METHOD
      startDate: $startDate
      endDate: $endDate
      pageSize: 100
    ) {
      items {
        destinationFacilityId
        destinationFacilityName
        managementMethodCode
        vendorAmount
        eManifestAmount
        documentAmount
        shipmentCount
        lastShipmentDate
      }
      count
    }
  }
`);

interface MethodRow {
  code: string;
  amount: number;
  shipments: number;
}

interface DestinationGroup {
  id: string | null;
  name: string | null;
  methods: MethodRow[];
  lastShipmentDate: string | null;
}

// GraphQL `Date` is a date-only UTC string; format it as UTC so users west of
// UTC don't see the previous calendar day.
const formatDate = (date: string | null) => (date ? getUtcDisplayDate(date, "med") : "—");

const shipmentLabel = (count: number) => `${count} shipment${count === 1 ? "" : "s"}`;

// One management method as an intentional metric: the method (code · description)
// as the small label above, the amount as the larger primary value with
// shipments beside it. Hand-rolled rather than StatDisplay because the label
// must wrap — management-method descriptions run long and StatDisplay's label is
// single-line (would clip them).
const MethodLine = ({ method }: { method: MethodRow }) => {
  const description = getManagementMethodCodeDescription(method.code);
  const code = method.code || "—";
  return (
    <Stack spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {description ? `${code} · ${description}` : code}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="h6">{formatAmountInPounds(method.amount) || "—"}</Typography>
        <Typography variant="caption" color="text.secondary">
          {shipmentLabel(method.shipments)}
        </Typography>
      </Box>
    </Stack>
  );
};

export const DestinationCards = ({ wasteStreamId }: { wasteStreamId: string }) => {
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();

  const { data } = useSuspenseQuery(WASTE_STREAM_DESTINATION_CARDS, {
    variables: {
      wasteStreamId,
      view: deferredFilters.view,
      startDate: deferredFilters.startDate,
      endDate: deferredFilters.endDate,
    },
  });

  const items = data.wasteStreamDestinations?.items ?? [];

  // Group the per-(destination, method) rows into one card per destination.
  const destinations: DestinationGroup[] = [];
  const byDestination = new Map<string, DestinationGroup>();
  for (const item of items) {
    const key = `${item.destinationFacilityId}-${item.destinationFacilityName}`;
    let group = byDestination.get(key);
    if (!group) {
      group = {
        id: item.destinationFacilityId ?? null,
        name: item.destinationFacilityName ?? null,
        methods: [],
        lastShipmentDate: null,
      };
      byDestination.set(key, group);
      destinations.push(group);
    }
    group.methods.push({
      code: item.managementMethodCode || "—",
      amount: Math.max(item.vendorAmount ?? 0, item.eManifestAmount ?? 0, item.documentAmount ?? 0),
      shipments: item.shipmentCount ?? 0,
    });
    // Most-recent shipment across this destination's methods.
    if (
      item.lastShipmentDate &&
      (!group.lastShipmentDate || item.lastShipmentDate > group.lastShipmentDate)
    ) {
      group.lastShipmentDate = item.lastShipmentDate;
    }
  }

  if (destinations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No destinations for this waste stream.
      </Typography>
    );
  }

  // Many destinations → the card grid would scroll forever; use the grid.
  if (destinations.length > DESTINATION_CARD_THRESHOLD) {
    return (
      <DestinationsView wasteStreamId={wasteStreamId} viewType={DestinationViewType.Destinations} />
    );
  }

  // Roll-up per management method (summed across destinations).
  const methodRollup = new Map<string, { amount: number; shipments: number }>();
  for (const dest of destinations) {
    for (const method of dest.methods) {
      const entry = methodRollup.get(method.code) ?? { amount: 0, shipments: 0 };
      entry.amount += method.amount;
      entry.shipments += method.shipments;
      methodRollup.set(method.code, entry);
    }
  }

  return (
    <Stack spacing={2.5}>
      {/* Destinations — one card each, broken down by management method. */}
      <Box>
        <Typography variant="overline" color="text.secondary">
          Destinations
        </Typography>
        <Box
          sx={{
            mt: 1,
            display: "grid",
            // A lone destination spans the full width; multiples go two-up.
            gridTemplateColumns: destinations.length === 1 ? "1fr" : { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          {destinations.map((dest) => {
            // Only show a destination total when it splits across methods —
            // a single method's line already is the total.
            const hasMultipleMethods = dest.methods.length > 1;
            const totalAmount = dest.methods.reduce((sum, m) => sum + m.amount, 0);
            const totalShipments = dest.methods.reduce((sum, m) => sum + m.shipments, 0);
            return (
              <Box
                key={`${dest.id}-${dest.name}`}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="subtitle2">{dest.name || "—"}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {dest.id ? `EPA ID: ${dest.id}` : "—"}
                </Typography>
                <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                  {dest.methods.map((method) => (
                    <MethodLine key={method.code} method={method} />
                  ))}
                  {hasMultipleMethods && (
                    <Box sx={{ pt: 0.75, borderTop: 1, borderColor: "divider" }}>
                      <StatDisplay
                        label="Total"
                        value={formatAmountInPounds(totalAmount) || "—"}
                        subtext={shipmentLabel(totalShipments)}
                        valueProps={{ variant: "h6" }}
                      />
                    </Box>
                  )}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.5 }}
                >
                  Last shipment: {formatDate(dest.lastShipmentDate)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Management methods — per-method totals across the stream, on white. */}
      <Box>
        <Typography variant="overline" color="text.secondary">
          Management methods
        </Typography>
        <Box
          sx={{
            mt: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            p: 2,
            backgroundColor: "background.paper",
          }}
        >
          <Stack spacing={0.5}>
            {[...methodRollup.entries()].map(([code, totals]) => (
              <MethodLine
                key={code}
                method={{ code, amount: totals.amount, shipments: totals.shipments }}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
};
